import { Link, redirect, useActionData, useNavigation, useParams } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useQueries, useQuery, useSuspenseQueries } from "@tanstack/react-query";
import type { CommentPage, Entry, Me, Profile, RequestBody, UpdateEntryRequest } from "@repo/shared-types";
import { api } from "@/lib/api";
import { requireSessionWith } from "@/lib/guards";
import { heldBy, noneWhenMissing, queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { useSession } from "@/routes/root";
import { errorMessage } from "@/lib/session";
import { partLabel } from "@/lib/event-form";
import { canShareInDivision, entryParts, isScored, oneLevel } from "@/lib/results";
import { formatMoment, fromWallTime, viewerZone } from "@/lib/times";
import { ErrorNote } from "@/components/form";
import { ShareStep } from "@/components/part-dialog";
import {
  LevelStep,
  PartResult,
  RecordBar,
  WorkoutPartDialog,
  WorkoutSheet,
  useSendWorkout,
  useWorkout,
  type ShareAnswer,
} from "@/components/workout";
import { Conversation } from "@/components/conversation";
import { LogTeams } from "@/components/team-fields";
import { teamHoldingLevel, teamPath } from "@/lib/teams";
import { looksLikeAPicture, uploadPicture } from "@/lib/images";
import { toast } from "@/lib/toast";
import { BackLink } from "@/components/navigation";
import { PageTitle } from "@/components/brand";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * One entry in the log: the event as it was when the result was recorded,
 * and the result — which its author changes, unshares or deletes here,
 * including after leaving the space it came from. As on an event's page
 * (components/workout.tsx), the bar at the bottom starts editing; each part
 * is changed in its own sheet, and the bar's Save changes sends them all,
 * with its one level, its photo and — here alone — when it was done.
 * Sharing needs the space it came from, so it is offered only while the way
 * back is open, by Share.
 *
 * Only ever the caller's own: the api answers /t/me/entries for nobody else.
 *
 * While it is shared and its author is still in the space it came from, the
 * thread on it is read here too — and only read: writing from the personal
 * space would put two spaces in one transaction, so replying is done on the
 * result's own page in its space.
 *
 * A part done in teams shows each team its author is on through it (0027):
 * its score — changed on the team's page, never here — their own note on
 * it, which is theirs to change here, and the team's thread, read the same
 * way while its score is on its board and they are still in its space.
 */

/** Whether the thread on the entry itself can be read from here: shared, and its space still open. */
function threadReadable(entry: Entry): boolean {
  return entry.shared && entry.origin !== null;
}

/** The ids of the teams the entry's parts were done in, whose threads may be read here. */
function teamIdsOf(entry: Entry): string[] {
  return entry.parts.flatMap((part) => part.teams.map((team) => team.id));
}

export async function entryLoader({ request, params }: LoaderFunctionArgs) {
  const id = params.entry ?? "";
  const [, [entry]] = await requireSessionWith(request, () =>
    Promise.all([read(queries.entry(id)), read(queries.profile())]),
  );
  await Promise.all([
    threadReadable(entry) ? read(queries.entryComments(id)).catch(noneWhenMissing) : null,
    ...teamIdsOf(entry).map((teamId) => read(queries.teamComments(teamId)).catch(noneWhenMissing)),
  ]);
  return null;
}

/**
 * What the page says back: an error, beside nothing else. Everything that
 * worked here — changes saved, sharing and unsharing, a
 * note on a team, a photo tried again, the result deleted — shows on the page
 * only as what was typed settling, so it is said by a toast from the action
 * (decided with Tiago 12 October).
 */
type ActionResult = {
  error?: string;
  /** The workout was sent: what the page typed is now what it reads. */
  saved?: boolean;
  /** It was, and the photo chosen with it did not go up. */
  photoFailed?: boolean;
  photoAdded?: boolean;
};

export async function entryAction({ request, params }: ActionFunctionArgs): Promise<ActionResult | Response> {
  const form = await request.formData();
  const id = params.entry ?? "";

  try {
    switch (String(form.get("intent") ?? "")) {
      // Every part changed while editing, by the entry's own part ids, and
      // what covers the whole workout (EntryView's send); then, when asked,
      // the share — through its space, which the personal one cannot do —
      // and the photo chosen, or the one saved taken down.
      case "record": {
        let body: RequestBody<UpdateEntryRequest>;
        try {
          body = JSON.parse(String(form.get("body") ?? ""));
        } catch {
          return { error: "Your result could not be read. Try again." };
        }
        // No division: the api copies the profile's in as it saves.
        const saved = await api.entries.update(id, body);
        let shared = false;
        if (form.get("share") !== null) {
          if (!saved.origin) return { saved: true, error: "Saved — but its space is no longer yours to share into." };
          await api.space(saved.origin.spaceSlug).events.entry.share(saved.origin.eventId);
          shared = true;
        }
        const file = form.get("file");
        if (form.get("removePhoto") !== null) await api.entries.photo.remove(id);
        if (looksLikeAPicture(file)) {
          try {
            await uploadPicture({
              start: () => api.entries.photo.start(id),
              confirm: (mediaId) => api.space("me").media.uploaded(mediaId),
              file,
            });
          } catch (error) {
            return { saved: true, photoFailed: true, error: `Saved, but the photo did not go up: ${errorMessage(error, "try it again")}.` };
          }
        }
        const said = (ok: string): ActionResult => {
          toast(ok);
          return { saved: true };
        };
        if (shared) return said("Saved, and on the leaderboard.");
        if (saved.shared || !saved.spaceName) return said("Saved.");
        const profile = await api.me.profile.get();
        if (!canShareInDivision(saved.parts, profile.division)) {
          return said("Saved, privately. Set your division in Account to show it on a leaderboard.");
        }
        return said("Saved. Only you can see it, in your log.");
      }

      // Everything comes off — notes, feel and photo too — so sharing again
      // asks afresh what goes with it (decided with Tiago 13 October).
      case "unshare":
        await api.entries.unshare(id);
        await api.entries.update(id, { detailsShared: false });
        toast("Taken off the leaderboard. It stays in your log.");
        return {};

      case "team-note": {
        const note = String(form.get("note") ?? "").trim();
        const feel = String(form.get("feel") ?? "");
        await api.entries.teams.note(String(form.get("team") ?? ""), {
          ...(note ? { note } : {}),
          ...(feel ? { feel } : {}),
        });
        toast("Saved your note on the team.");
        return {};
      }

      // The photo, put up the moment it is picked, or tried again (7g):
      // shrunk here, posted to the store, confirmed. One that does not go up
      // stays on the page to be tried again.
      case "photo": {
        const file = form.get("file");
        if (!looksLikeAPicture(file)) return { error: "Choose a picture to add." };
        try {
          await uploadPicture({
            start: () => api.entries.photo.start(id),
            confirm: (mediaId) => api.space("me").media.uploaded(mediaId),
            file,
          });
        } catch (error) {
          return { photoFailed: true, error: `Your photo did not go up: ${errorMessage(error, "try it again")}.` };
        }
        toast("Photo added. It shows for 24 hours from your first.");
        return { photoAdded: true };
      }


      // Said before going: the log shows it only by its absence.
      case "delete-entry":
        await api.entries.delete(id);
        toast("Your result is deleted.");
        return redirect("/t/me");

      default:
        return { error: "Unknown action" };
    }
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}

export default function EntryView() {
  const id = useParams().entry ?? "";
  const session = useSession();
  const [{ data: entry }, { data: profile }] = useSuspenseQueries({ queries: [queries.entry(id), queries.profile()] });
  // A 404 is an answer, and a loader that got one left it as it was: not asked again on mounting.
  const thread = heldBy(
    useQuery({ ...queries.entryComments(id), enabled: threadReadable(entry), retryOnMount: false }),
  );
  const teamIds = teamIdsOf(entry);
  const teamPages = useQueries({
    queries: teamIds.map((teamId) => ({ ...queries.teamComments(teamId), retryOnMount: false })),
  });
  const teamThreads: Record<string, CommentPage> = {};
  teamIds.forEach((teamId, index) => {
    const page = heldBy(teamPages[index]!);
    if (page) teamThreads[teamId] = page;
  });
  // The loader's guard let the page draw only with a session.
  return session && <EntryPage {...{ entry, profile, thread, teamThreads, session }} />;
}

function EntryPage({
  entry,
  profile,
  thread,
  teamThreads,
  session,
}: {
  entry: Entry;
  profile: Profile;
  /** The thread on it, while it can be read from here; null otherwise. */
  thread: CommentPage | null;
  /** Each of its teams' threads, by the team's id, while one can be read from here. */
  teamThreads: Record<string, CommentPage>;
  session: Me;
}) {
  const { zone } = viewerZone(profile.timeZone);
  const [meId, images] = [session.user.id, session.images];
  const result = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const busy = navigation.state === "submitting";
  const recordable = entryParts(entry);
  const single = oneLevel(entry);
  // In an event ranked overall, a team the viewer is on holds their level (0037).
  const heldOn = single ? entry.parts.findIndex((part) => teamHoldingLevel(part.teams) !== null) : -1;
  const holding = heldOn >= 0 ? teamHoldingLevel(entry.parts[heldOn]!.teams) : null;
  const levelHeldBy =
    holding?.level ? { part: `${partLabel(heldOn)}. ${entry.parts[heldOn]!.title}`, level: holding.level } : null;
  // Each part changed in its sheet while editing, kept until the bar's Save
  // changes sends them all (decided with Tiago 13 October).
  const workout = useWorkout({ savedLevel: entry.level, registered: true, single, outcome: result });
  const post = useSendWorkout(workout);
  // Sharing needs its space: offered only while the way back is open, and
  // with a division, since every board is men's or women's.
  const division = canShareInDivision(entry.parts, profile.division);
  const shareable = entry.origin !== null && division;

  /** Sends what changed: every part by the entry's own ids, when it was done, and the answer to Share. */
  const send = (answer: ShareAnswer | null) => {
    const list = Object.values(workout.drafts);
    const performedAt = workout.doneAt !== null ? fromWallTime(workout.doneAt, zone) : null;
    const body: RequestBody<UpdateEntryRequest> = {
      ...(single ? { level: workout.level } : {}),
      ...(performedAt ? { performedAt } : {}),
      ...(answer?.share ? { detailsShared: answer.detailsShared } : {}),
      parts: list.map((draft) => ({
        entryPartId: draft.part.id,
        level: draft.part.level,
        // The badge rides on Rx alone, and the event's one level is chosen once.
        rxPlus: single && draft.part.rxPlus !== null && workout.level !== "rx" ? false : draft.part.rxPlus,
        scores: draft.part.scores,
      })),
      notes: list.flatMap((draft) =>
        draft.note ? [{ entryPartId: draft.note.id, note: draft.note.note, feel: draft.note.feel }] : [],
      ),
    };
    post(body, answer?.share ? { share: "1" } : {});
  };
  const openPart = recordable.find((part) => part.id === workout.open);

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <BackLink to="/t/me">Your log</BackLink>
        <PageTitle of={[entry.title]} />
        <h1 className="page-title">{entry.title}</h1>
        <p className="text-muted-foreground">
          {[entry.spaceName, entry.formatLabel, entry.sportLabel].filter(Boolean).join(" · ")}
          {entry.startsAt && ` · ${formatMoment(entry.startsAt, zone)}`}
        </p>
        {entry.origin ? (
          <Link
            to={`/t/${entry.origin.spaceSlug}/events/${entry.origin.eventId}`}
            className="text-sm underline underline-offset-4"
          >
            Open the original
          </Link>
        ) : entry.spaceName ? (
          <p className="text-sm text-muted-foreground">
            The original is no longer yours to open — you left {entry.spaceName}, or it was deleted. This copy
            stays yours.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Logged on your own. It stays in your log, and is never shared.</p>
        )}
      </div>

      {result?.error && <ErrorNote message={result.error} />}

      <section className="space-y-3" aria-label="The workout">
        <h2 className="section-label">
          The workout, as it was{recordable.some(isScored) ? ", and your result" : ""}
        </h2>
        {entry.description && (
          <Card size="sm">
            <CardContent>
              <p className="text-base whitespace-pre-line text-foreground">{entry.description}</p>
            </CardContent>
          </Card>
        )}
        <ul className="space-y-3">
          {entry.parts.map((part, index) => (
            <li key={part.id}>
              <Card size="sm" className="gap-3">
                <CardHeader>
                  <CardTitle>
                    {partLabel(index)}. {part.title}
                  </CardTitle>
                  <CardDescription>{part.summary}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 empty:hidden">
                  {part.description && <p className="whitespace-pre-line text-foreground">{part.description}</p>}
                  {part.isTeam ? (
                    // A team's score is changed on its page; your note on it, here.
                    <LogTeams
                      teams={part.teams}
                      meId={meId}
                      spaceName={entry.spaceName}
                      reachable={entry.origin !== null}
                      busy={busy}
                    />
                  ) : (
                    recordable[index] && (
                      <PartResult
                        part={recordable[index]}
                        draft={workout.drafts[part.id]}
                        registered
                        recording={workout.recording}
                        showLevel={!entry.level}
                        spaceName={entry.spaceName}
                        onOpen={() => workout.setOpen(part.id)}
                        onUndo={() => workout.setDraft(part.id, undefined)}
                      />
                    )
                  )}
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      </section>

      {workout.sheet && (
        <WorkoutSheet
          entry={entry}
          workout={workout}
          zone={zone}
          single={single}
          shareable={shareable}
          noDivision={entry.spaceName !== null && !division}
          profileDivision={profile.division}
          spaceName={entry.spaceName}
          photoFailed={Boolean(result?.photoFailed)}
          busy={busy}
          images={images}
          doneAtEditable
          onShare={() => workout.setAsking(true)}
        />
      )}

      {openPart && (
        <WorkoutPartDialog
          // A fresh dialog each time: it starts from the part as it stands.
          key={openPart.id}
          part={openPart}
          index={recordable.indexOf(openPart)}
          workout={workout}
          single={single}
        />
      )}

      {workout.askingLevel && (
        <LevelStep
          eventTitle={entry.title}
          level={workout.level}
          registered
          levelHeldBy={levelHeldBy}
          onChoose={(level) => {
            workout.setLevel(level);
            workout.setAskingLevel(false);
          }}
          onClose={() => workout.setAskingLevel(false)}
        />
      )}

      {workout.asking && entry.spaceName && (
        <ShareStep spaceName={entry.spaceName} busy={busy} onAnswer={send} onClose={() => workout.setAsking(false)} />
      )}

      {thread && entry.origin && (
        <Conversation
          key={entry.id}
          target={{ slug: "me", eventId: entry.origin.eventId, entryId: entry.id }}
          page={thread}
          reactions={null}
          writes={false}
          replyIn={{
            to: `/t/${entry.origin.spaceSlug}/events/${entry.origin.eventId}/results/${entry.id}`,
            name: entry.spaceName ?? "its space",
          }}
        />
      )}

      {entry.parts.flatMap((part) =>
        part.teams
          .filter((team) => teamThreads[team.id] && team.spaceSlug && team.eventId)
          .map((team) => (
            <Conversation
              key={team.id}
              title={`${part.title}: the team's conversation`}
              target={{ slug: "me", eventId: team.eventId ?? "", entryId: null, teamId: team.id }}
              page={teamThreads[team.id]!}
              reactions={null}
              writes={false}
              replyIn={{
                to: teamPath(`/t/${team.spaceSlug}`, team.eventId ?? "", team.id),
                name: entry.spaceName ?? "its space",
              }}
            />
          )),
      )}

      <RecordBar workout={workout} entry={entry} busy={busy} onSend={() => send(null)} />
    </section>
  );
}
